import { useState, useEffect, useCallback } from 'react';
import { getMessages } from '../utils/velvypalApi';
import { logger } from '../utils/logger';

/**
 * Hook for managing velvypal messages
 * @param {Object|null} user - Firebase user object
 * @returns {{
 *   freeMessages: number,
 *   purchasedMessages: number,
 *   totalMessages: number,
 *   lastReset: string|null,
 *   loading: boolean,
 *   error: string|null,
 *   refreshMessages: Function
 * }}
 */
export function useMessages(user) {
  const [messages, setMessages] = useState({
    free: 3,
    purchased: 0,
    total: 3,
    lastReset: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async () => {
    if (!user) {
      setMessages({ free: 3, purchased: 0, total: 3, lastReset: null });
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getMessages(user);
      setMessages({
        free: data.free_messages ?? 3,
        purchased: data.purchased_messages ?? 0,
        total: data.total_messages ?? 3,
        lastReset: data.last_reset,
      });
    } catch (err) {
      logger.error('Failed to fetch messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Refresh function for external use
  const refreshMessages = useCallback(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    freeMessages: messages.free,
    purchasedMessages: messages.purchased,
    totalMessages: messages.total,
    lastReset: messages.lastReset,
    loading,
    error,
    refreshMessages,
  };
}
